import CurrentUserContext from "@/Contexts/CurrentUser";
import { displayNotification } from "@/utils/notifications";
import { Tooltip, IconButton, Badge } from "@mui/material"
import { useContext } from "react";
import CloudSyncTwoToneIcon from '@mui/icons-material/CloudSyncTwoTone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const SharePointConnector: React.FC = () => {
  const { currentUser, refetchCurrentUser } = useContext(CurrentUserContext);
  const handleSharePointConnect = async () => {
    try {
      const authUrl = `${import.meta.env.VITE_DB_URL}/sharepoint/authorize?site_url=root`;

      // fetch the authorization URL from backend
      const response = await fetch(authUrl, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        displayNotification({
          type: "error",
          text: errorData.error || 'Failed to initiate SharePoint connection',
        });
        return;
      }

      const data = await response.json();
      const microsoftAuthUrl = data.authorization_url;

      if (!microsoftAuthUrl) {
        displayNotification({
          type: "error",
          text: 'Invalid response from server',
        });
        return;
      }

      // open the Microsoft OAuth URL in a popup window
      const popup = window.open(
        microsoftAuthUrl,
        'SharePoint OAuth',
        'width=600,height=700,left=' + (window.screen.width / 2 - 300) + ',top=' + (window.screen.height / 2 - 350)
      );

      if (!popup) {
        displayNotification({
          type: "error",
          text: "Please allow popups for this site to connect to SharePoint",
        });
        return;
      }

      // listen for messages from the popup
      const messageHandler = (event: MessageEvent) => {
        if (event.data?.type === 'sharepoint_oauth_success') {
          displayNotification({
            type: "success",
            text: "SharePoint connected successfully",
          });
          refetchCurrentUser && refetchCurrentUser();
          window.removeEventListener('message', messageHandler);
        } else if (event.data?.type === 'sharepoint_oauth_error') {
          displayNotification({
            type: "error",
            text: event.data.message || `SharePoint connection failed: ${event.data.error}`,
          });
          window.removeEventListener('message', messageHandler);
        }
      };

      window.addEventListener('message', messageHandler);

      // also monitor popup for manual closure (user might close without completing)
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageHandler);
        }
      }, 500);
    } catch (error: any) {
      displayNotification({
        type: "error",
        text: error.message || "Failed to initiate SharePoint connection",
      });
    }
  };

  return (
    <Tooltip title={currentUser?.sharepointConnectionActive ? "SharePoint Connected" : "Connect to SharePoint"}>
      <span>
        <IconButton
          onClick={handleSharePointConnect}
          disabled={currentUser?.sharepointConnectionActive || false}
        >
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              currentUser?.sharepointConnectionActive ? (
                <CheckCircleIcon
                  sx={{
                    fontSize: '14px',
                    color: '#4caf50',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    padding: '1px',
                    boxSizing: 'border-box'
                  }}
                />
              ) : null
            }
          >
            <CloudSyncTwoToneIcon sx={{ color: 'white' }} />
          </Badge>
        </IconButton>
      </span>
    </Tooltip>
  )
}

export default SharePointConnector
